// Print-preview helpers for the POS terminal (Electron).
//
// The POS is the only place that can produce a 100%-accurate preview, because it
// runs the SAME raster pipeline it prints with. These helpers fetch the sample
// receipt image from the backend and hand it to the Electron renderer, which
// returns the EXACT 1-bit raster (as a PNG) the printer would receive.
//
// Used by:
//  - RestaurantLayout: responds to an admin's live-preview socket request.
//  - PrinterSettings: a local "Preview" button the restaurant can use directly.

import { restaurantAPI } from '../services/api';

// Backend origin (POS loads the site; dev ports 3001/3002 → API on 3000).
function apiBase() {
  return window.location.origin.replace(':3001', ':3000').replace(':3002', ':3000');
}

// Build the sample receipt/KOT image URL the terminal fetches + rasters.
// restaurantId is passed so multi-outlet owners get the correct outlet's header
// (the backend validates the caller owns it, else falls back to their restaurant).
export function buildPreviewImageUrl(settings = {}, kind = 'receipt', restaurantId = '') {
  const pw = settings.paperWidth || '58';
  const fs = settings.fontSize || '9';
  const fw = settings.fontWeight || 'bold';
  const lh = settings.lineHeight || '1.15';
  const ml = settings.marginLeft || '2';
  const mr = settings.marginRight || '2';
  const qs = `paperWidth=${pw}&fontSize=${fs}&fontWeight=${fw}&lineHeight=${lh}&marginLeft=${ml}&marginRight=${mr}${kind === 'kot' ? '&kind=kot' : ''}${restaurantId ? `&restaurantId=${encodeURIComponent(restaurantId)}` : ''}`;
  return `${apiBase()}/api/orders/receipt-image-preview?${qs}`;
}

// Render the EXACT print preview on THIS terminal. Electron only.
// Returns { success, dataUrl?, width?, height?, error? }.
export async function renderExactPreview(settings = {}, kind = 'receipt', restaurantId = '') {
  if (!window.electronAPI?.renderReceiptImagePreview) {
    // Distinguish "not the POS app" from "POS app too old to render previews".
    const msg = window.electronAPI?.isElectron
      ? 'This POS terminal needs an update (v1.0.16+) to render previews.'
      : 'Preview is only available on the POS desktop terminal.';
    return { success: false, error: msg };
  }
  const token = localStorage.getItem('token') || '';
  const url = buildPreviewImageUrl(settings, kind, restaurantId);
  // Same fields print-receipt-image uses for the mono/raster conversion.
  const printSettings = {
    printerType: settings.printerType || 'escpos',
    paperWidth: settings.paperWidth || '58',
    marginBottom: settings.marginBottom || '0',
    rasterBandHeight: settings.rasterBandHeight || '0',
  };
  try {
    const res = await window.electronAPI.renderReceiptImagePreview(url, printSettings, token);
    return res || { success: false, error: 'No response from renderer' };
  } catch (e) {
    return { success: false, error: String(e?.message || e) };
  }
}

// Respond to an admin's live-preview socket request: render + upload the result.
export async function respondToPreviewRequest(data) {
  const { requestId, settings, kind, restaurantId } = data || {};
  if (!requestId) return;
  const res = await renderExactPreview(settings || {}, kind, restaurantId || '');
  try {
    if (res.success && res.dataUrl) {
      await restaurantAPI.uploadPrintPreview({ requestId, dataUrl: res.dataUrl, kind });
    } else {
      await restaurantAPI.uploadPrintPreview({ requestId, error: res.error || 'Preview failed', kind });
    }
  } catch (_) {
    // best-effort; admin will time out and see "no response"
  }
}
